/**
 * FaintSplatFilterPanel: Runtime controls to hide sky/background artifact splats.
 */
export const SKY_CLEANUP_PRESET = {
    minAlpha: 5,
    maxScaleSlider: 25,
    hideBlue: true
};

export class FaintSplatFilterPanel {

    constructor(container, onChange) {
        this.container = container || document.body;
        this.onChange = onChange;
        this.minAlpha = 0;
        this.maxScaleSlider = 0;
        this.hideBlue = false;
        this.areaOfInterestPercent = 100;
        this.areaOfInterestMaxRadius = 0;
        this.setupDOM();
    }

    setupDOM() {
        this.domContainer = document.createElement('div');
        this.domContainer.style.pointerEvents = 'auto';
        this.domContainer.style.position = 'absolute';
        this.domContainer.style.bottom = '20px';
        this.domContainer.style.right = '20px';
        this.domContainer.style.zIndex = '9997';

        const style = document.createElement('style');
        style.innerHTML = `
            .splat-filter-panel {
                padding: 10px 14px;
                background-color: rgba(30, 30, 30, 0.85);
                border: 1px solid #666;
                border-radius: 8px;
                color: #eeeeee;
                font-family: arial, sans-serif;
                font-size: 11pt;
                min-width: 260px;
            }

            .splat-filter-settings-button {
                width: 44px;
                height: 44px;
                padding: 0;
                border: 1px solid #666;
                border-radius: 50%;
                background-color: rgba(30, 30, 30, 0.9);
                color: #eeeeee;
                font-size: 22px;
                line-height: 1;
                cursor: pointer;
            }

            .splat-filter-settings-button:hover {
                background-color: rgba(60, 60, 60, 0.95);
                border-color: #888;
            }

            .splat-filter-panel label {
                display: block;
                margin-bottom: 6px;
            }

            .splat-filter-panel input[type="range"] {
                width: 100%;
                cursor: pointer;
                margin-bottom: 10px;
            }

            .splat-filter-panel .splat-filter-row {
                margin-bottom: 4px;
            }

            .splat-filter-panel .splat-filter-hint {
                margin-top: 6px;
                font-size: 9pt;
                color: #aaaaaa;
                line-height: 1.3;
            }

            .splat-filter-panel .splat-filter-checkbox {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-top: 4px;
                font-size: 10pt;
                cursor: pointer;
            }

            .splat-filter-panel .splat-filter-buttons {
                display: flex;
                gap: 8px;
                margin-top: 10px;
            }

            .splat-filter-panel .splat-filter-button {
                flex: 1;
                padding: 6px 10px;
                border: 1px solid #666;
                border-radius: 5px;
                background-color: rgba(60, 60, 60, 0.9);
                color: #eeeeee;
                font-family: arial, sans-serif;
                font-size: 10pt;
                cursor: pointer;
            }

            .splat-filter-panel .splat-filter-button:hover {
                background-color: rgba(80, 80, 80, 0.95);
                border-color: #888;
            }

            .splat-filter-panel .splat-filter-button-primary {
                border-color: #4a9eff;
                color: #cce4ff;
            }
        `;
        this.domContainer.appendChild(style);

        this.settingsButton = document.createElement('button');
        this.settingsButton.type = 'button';
        this.settingsButton.className = 'splat-filter-settings-button';
        this.settingsButton.textContent = '⚙';
        this.settingsButton.title = 'Splat filter settings';
        this.settingsButton.setAttribute('aria-label', 'Toggle splat filter settings');
        this.settingsButton.setAttribute('aria-expanded', 'false');
        this.settingsButton.addEventListener('click', () => this.toggle());
        this.domContainer.appendChild(this.settingsButton);

        this.panelElement = document.createElement('div');
        this.panelElement.className = 'splat-filter-panel';
        this.panelElement.hidden = true;

        this.minAlphaLabel = document.createElement('label');
        this.minAlphaLabel.textContent = 'Hide faint splats: 0 (off)';
        this.panelElement.appendChild(this.minAlphaLabel);

        this.minAlphaSlider = document.createElement('input');
        this.minAlphaSlider.type = 'range';
        this.minAlphaSlider.min = '0';
        this.minAlphaSlider.max = '255';
        this.minAlphaSlider.step = '1';
        this.minAlphaSlider.value = '0';
        this.minAlphaSlider.addEventListener('input', () => this.notifyChange());
        this.panelElement.appendChild(this.minAlphaSlider);

        this.maxScaleLabel = document.createElement('label');
        this.maxScaleLabel.textContent = 'Hide large splats: 0 (off)';
        this.panelElement.appendChild(this.maxScaleLabel);

        this.maxScaleSliderElement = document.createElement('input');
        this.maxScaleSliderElement.type = 'range';
        this.maxScaleSliderElement.min = '0';
        this.maxScaleSliderElement.max = '300';
        this.maxScaleSliderElement.step = '1';
        this.maxScaleSliderElement.value = '0';
        this.maxScaleSliderElement.addEventListener('input', () => this.notifyChange());
        this.panelElement.appendChild(this.maxScaleSliderElement);

        this.blueCheckboxLabel = document.createElement('label');
        this.blueCheckboxLabel.className = 'splat-filter-checkbox';
        this.blueCheckbox = document.createElement('input');
        this.blueCheckbox.type = 'checkbox';
        this.blueCheckbox.addEventListener('change', () => this.notifyChange());
        this.blueCheckboxLabel.appendChild(this.blueCheckbox);
        this.blueCheckboxLabel.appendChild(document.createTextNode('Hide blue-tinted splats'));
        this.panelElement.appendChild(this.blueCheckboxLabel);

        this.areaOfInterestLabel = document.createElement('label');
        this.areaOfInterestLabel.textContent = 'Focus area: 100% (full scene)';
        this.panelElement.appendChild(this.areaOfInterestLabel);

        this.areaOfInterestSlider = document.createElement('input');
        this.areaOfInterestSlider.type = 'range';
        this.areaOfInterestSlider.min = '5';
        this.areaOfInterestSlider.max = '100';
        this.areaOfInterestSlider.step = '1';
        this.areaOfInterestSlider.value = '100';
        this.areaOfInterestSlider.addEventListener('input', () => this.notifyChange());
        this.panelElement.appendChild(this.areaOfInterestSlider);

        const buttonRow = document.createElement('div');
        buttonRow.className = 'splat-filter-buttons';

        this.skyCleanupButton = document.createElement('button');
        this.skyCleanupButton.type = 'button';
        this.skyCleanupButton.className = 'splat-filter-button splat-filter-button-primary';
        this.skyCleanupButton.textContent = 'Sky cleanup';
        this.skyCleanupButton.addEventListener('click', () => this.applySkyCleanupPreset());
        buttonRow.appendChild(this.skyCleanupButton);

        this.resetButton = document.createElement('button');
        this.resetButton.type = 'button';
        this.resetButton.className = 'splat-filter-button';
        this.resetButton.textContent = 'Reset';
        this.resetButton.addEventListener('click', () => this.resetFilters());
        buttonRow.appendChild(this.resetButton);

        this.panelElement.appendChild(buttonRow);

        this.hintElement = document.createElement('div');
        this.hintElement.className = 'splat-filter-hint';
        this.hintElement.textContent = 'Focus area centers on the densest points; lower % hides distant splats';
        this.panelElement.appendChild(this.hintElement);

        this.domContainer.appendChild(this.panelElement);
    }

    notifyChange() {
        this.minAlpha = parseInt(this.minAlphaSlider.value, 10);
        this.maxScaleSlider = parseInt(this.maxScaleSliderElement.value, 10);
        this.hideBlue = this.blueCheckbox.checked;
        this.areaOfInterestPercent = parseInt(this.areaOfInterestSlider.value, 10);
        this.updateLabels();
        if (this.onChange) {
            this.onChange({
                minAlpha: this.minAlpha,
                maxScale: this.maxScaleSlider / 1000.0,
                hideBlue: this.hideBlue,
                areaOfInterestPercent: this.areaOfInterestPercent
            });
        }
    }

    updateLabels() {
        this.minAlphaLabel.textContent = this.minAlpha <= 0 ?
            'Hide faint splats: 0 (off)' : `Hide faint splats: ${this.minAlpha}`;
        this.maxScaleLabel.textContent = this.maxScaleSlider <= 0 ?
            'Hide large splats: 0 (off)' : `Hide large splats: ${this.maxScaleSlider}`;
        if (this.areaOfInterestPercent >= 100) {
            this.areaOfInterestLabel.textContent = 'Focus area: 100% (full scene)';
        } else {
            const radius = this.areaOfInterestMaxRadius * (this.areaOfInterestPercent / 100.0);
            this.areaOfInterestLabel.textContent = `Focus area: ${this.areaOfInterestPercent}% (~${radius.toFixed(2)} units)`;
        }
    }

    applySkyCleanupPreset() {
        this.setValues(SKY_CLEANUP_PRESET, true);
    }

    resetFilters() {
        this.setValues({ minAlpha: 0, maxScaleSlider: 0, hideBlue: false, areaOfInterestPercent: 100 }, true);
    }

    updateAreaOfInterestMaxRadius(maxRadius) {
        this.areaOfInterestMaxRadius = maxRadius;
        this.updateLabels();
    }

    setOpen(isOpen) {
        this.panelElement.hidden = !isOpen;
        this.settingsButton.setAttribute('aria-expanded', String(isOpen));
    }

    toggle() {
        this.setOpen(this.panelElement.hidden);
    }

    setContainer(container) {
        if (this.container && this.domContainer.parentElement === this.container) {
            this.container.removeChild(this.domContainer);
        }
        this.container = container || document.body;
        if (this.container) {
            this.container.appendChild(this.domContainer);
        }
    }

    setValues({ minAlpha = 0, maxScaleSlider = 0, hideBlue = false, areaOfInterestPercent = 100 }, notify = false) {
        this.minAlpha = Math.max(0, Math.min(255, minAlpha));
        this.maxScaleSlider = Math.max(0, Math.min(300, maxScaleSlider));
        this.hideBlue = hideBlue;
        this.areaOfInterestPercent = Math.max(5, Math.min(100, areaOfInterestPercent));
        this.minAlphaSlider.value = String(this.minAlpha);
        this.maxScaleSliderElement.value = String(this.maxScaleSlider);
        this.blueCheckbox.checked = this.hideBlue;
        this.areaOfInterestSlider.value = String(this.areaOfInterestPercent);
        this.updateLabels();
        if (notify && this.onChange) {
            this.onChange({
                minAlpha: this.minAlpha,
                maxScale: this.maxScaleSlider / 1000.0,
                hideBlue: this.hideBlue,
                areaOfInterestPercent: this.areaOfInterestPercent
            });
        }
    }

    getValues() {
        return {
            minAlpha: this.minAlpha,
            maxScaleSlider: this.maxScaleSlider,
            hideBlue: this.hideBlue,
            areaOfInterestPercent: this.areaOfInterestPercent
        };
    }

    dispose() {
        if (this.domContainer && this.domContainer.parentElement) {
            this.domContainer.parentElement.removeChild(this.domContainer);
        }
    }

}
